/**
 * Bản tiếng Việt cho lớp "gần tôi" — đợt 2: toàn bộ dịch vụ ngoài 9 cụm chủ lực.
 *
 * Biên dịch thủ công (không tốn AI credit). Nội dung khớp ./en-copy-2.ts.
 *  - Thái Lan không phải thành viên Công ước Hague Apostille; giấy tờ Thái dùng
 *    ở nước ngoài đi qua Cục Lãnh sự (Bộ Ngoại giao) và, khi cần, đại sứ quán
 *    nước đến tại Thái Lan.
 *  - Không niêm yết giá, báo giá theo từng hồ sơ; không cam kết thời gian hay
 *    kết quả — quyền chấp nhận thuộc cơ quan tiếp nhận.
 *  - Chúng tôi không chỉ "làm thay": đội ngũ hơn 15 năm kinh nghiệm giấy tờ
 *    xuyên biên giới tư vấn trình tự và chứng cứ trước khi bắt tay vào việc.
 */

import type { NearServiceKey } from "./registry";
import type { NearViCopy } from "./vi-copy";

export const NEAR_VI2_KEYS = [
  "marriage",
  "longstay",
  "corporate",
  "immigration",
  "estate",
  "civilreg",
  "family",
  "accounting",
  "interpreter",
  "embassy",
  "ip",
  "litigation",
  "pdpa",
  "misc-official",
  "council",
  "due-diligence",
  "pension",
  "work-permit",
  "expert",
] as const satisfies readonly NearServiceKey[];

export type NearVi2Key = (typeof NEAR_VI2_KEYS)[number];

const MFA =
  "Khi nơi tiếp nhận yêu cầu, hồ sơ đi tiếp tới Cục Lãnh sự (Bộ Ngoại giao Thái Lan) rồi tới đại sứ quán nước đến tại Thái Lan.";

export const NEAR_VI2_COPY: Record<NearVi2Key, NearViCopy> = {
  marriage: {
    label: "Dịch vụ giấy tờ kết hôn Thái – nước ngoài",
    definition:
      "Đăng ký kết hôn giữa người Thái và người nước ngoài là một chuỗi giấy tờ chứ không phải một lần hẹn: xin giấy xác nhận đủ điều kiện kết hôn (độc thân) tại đại sứ quán của bạn ở Thái Lan, dịch tiếng Thái có chứng nhận, hợp pháp hóa bản dịch tại Bộ Ngoại giao, rồi đăng ký tại quận/huyện (amphoe).",
    chain: [
      "Xác nhận quy trình và cách đặt hẹn hiện hành của đại sứ quán bạn về giấy xác nhận độc thân.",
      "Nhận giấy của đại sứ quán, sau đó biên dịch viên có chứng nhận dịch sang tiếng Thái.",
      "Hợp pháp hóa bản dịch tại Cục Lãnh sự (Bộ Ngoại giao).",
      "Hai bên cùng có mặt đăng ký kết hôn tại văn phòng quận/huyện.",
      "Xin bản trích lục giấy chứng nhận kết hôn, dịch và hợp pháp hóa để dùng ở nước ngoài.",
    ],
    checklist: [
      "Hộ chiếu của bên nước ngoài kèm dấu nhập cảnh.",
      "CCCD và sổ hộ khẩu (tabien baan) của bên Thái.",
      "Giấy xác nhận đủ điều kiện kết hôn / độc thân do đại sứ quán cấp.",
      "Giấy ly hôn hoặc giấy chứng tử nếu từng kết hôn.",
      "Hai người làm chứng khi đăng ký tại quận/huyện.",
    ],
    pitfalls: [
      "Đặt lịch quận/huyện trước khi hoàn tất hợp pháp hóa tại Bộ Ngoại giao.",
      "Tên trên giấy xác nhận khác với hộ chiếu.",
      "Thiếu bằng chứng chấm dứt cuộc hôn nhân trước.",
      "Tưởng giấy kết hôn Thái đương nhiên được công nhận ở nước ngoài mà không cần dịch và hợp pháp hóa.",
    ],
    remote:
      "Dịch, hợp pháp hóa và sắp xếp trình tự chúng tôi lo; buổi hẹn đại sứ quán và lễ đăng ký cần hai bên có mặt.",
  },
  longstay: {
    label: "Hồ sơ gia hạn lưu trú dài hạn và hưu trí",
    definition:
      "Gia hạn lưu trú dài hạn do Cục Xuất nhập cảnh quyết định dựa trên chứng cứ nộp kèm. Vai trò của chúng tôi là chuẩn bị hồ sơ và tư vấn: khớp bộ chứng cứ với đúng diện, theo định dạng cơ quan chấp nhận.",
    chain: [
      "Xác định đúng diện gia hạn và danh mục chứng cứ hiện hành.",
      "So sánh với giấy tờ bạn đang có để tìm phần thiếu.",
      "Chuẩn bị chứng cứ tài chính, nơi ở và bảo hiểm theo định dạng được chấp nhận.",
      "Sắp xếp hồ sơ theo thứ tự nộp kèm bảng kiểm.",
      "Hỗ trợ lúc nộp và phản hồi khi bị yêu cầu bổ sung.",
    ],
    checklist: [
      "Hộ chiếu kèm visa hiện tại và TM.6 / dữ liệu nhập cảnh.",
      "Thông báo địa chỉ TM.30 và hợp đồng thuê hoặc giấy sở hữu.",
      "Giấy tờ ngân hàng theo định dạng và thời gian duy trì số dư được yêu cầu.",
      "Giấy tờ bảo hiểm y tế nếu diện đó yêu cầu.",
      "Giấy kết hôn hoặc chứng minh phụ thuộc với diện gia đình.",
    ],
    pitfalls: [
      "Chứng cứ ngân hàng chưa đủ thời gian duy trì theo quy định.",
      "Thiếu thông báo TM.30, thứ bị kiểm tra trước khi xét gia hạn.",
      "Nộp quá sát ngày hết hạn lưu trú.",
      "Tin rằng gia hạn có thể được bảo đảm — quyết định thuộc về cán bộ xét duyệt.",
    ],
    remote:
      "Chuẩn bị và rà soát hồ sơ từ xa; buổi hẹn tại Cục Xuất nhập cảnh phải có mặt.",
  },
  corporate: {
    label: "Dịch vụ giấy tờ doanh nghiệp",
    definition:
      "Giấy tờ doanh nghiệp gồm hồ sơ công ty, affidavit, trích lục DBD, nghị quyết hội đồng và việc chứng nhận chúng để dùng ở nước ngoài — thường phục vụ mở tài khoản, đấu thầu, hợp đồng phân phối hoặc lập công ty con.",
    chain: [
      "Xác nhận danh mục giấy tờ chính xác của bên tiếp nhận và độ mới yêu cầu.",
      "Xin trích lục DBD và affidavit mới.",
      "Soạn nghị quyết hoặc giấy ủy quyền theo câu chữ đối tác chấp nhận.",
      "Công chứng chữ ký và bản sao khi cần.",
      MFA,
    ],
    checklist: [
      "Affidavit công ty và giấy chứng nhận DBD (cấp gần đây).",
      "Danh sách cổ đông và trích lục ngành nghề kinh doanh.",
      "Hộ chiếu hoặc CCCD của các giám đốc có thẩm quyền.",
      "Dự thảo nghị quyết hoặc hợp đồng ở bản cuối cùng.",
      "Yêu cầu bằng văn bản của đối tác về bộ giấy tờ.",
    ],
    pitfalls: [
      "Trích lục cũ hơn thời hạn hiệu lực đối tác quy định.",
      "Ký trước buổi hẹn chứng nhận.",
      "Nghị quyết do giám đốc không có thẩm quyền theo affidavit ký.",
      "Bản dịch bỏ sót chữ trên con dấu của trích lục tiếng Thái.",
    ],
    remote: "Mọi việc trừ khâu chứng kiến ký đều làm từ xa được.",
  },
  immigration: {
    label: "Chuẩn bị hồ sơ và tư vấn xuất nhập cảnh",
    definition:
      "Các thủ tục xuất nhập cảnh — gia hạn, re-entry permit, đổi diện lưu trú, khai báo — mỗi loại có danh mục chứng cứ riêng. Chúng tôi chuẩn bị và sắp xếp trình tự hồ sơ; Cục Xuất nhập cảnh là nơi quyết định.",
    chain: [
      "Xác nhận loại thủ tục, văn phòng có thẩm quyền và danh mục chứng cứ.",
      "Rà soát thời hạn lưu trú hiện tại và các hồ sơ đã nộp trước để bảo đảm nhất quán.",
      "Chuẩn bị và chứng nhận giấy tờ kèm theo.",
      "Sắp xếp theo thứ tự nộp kèm bảng kiểm tại quầy.",
      "Theo dõi và phản hồi khi bị yêu cầu bổ sung.",
    ],
    checklist: [
      "Hộ chiếu kèm toàn bộ trang visa và dấu.",
      "Thông báo địa chỉ TM.30 và giấy tờ chỗ ở.",
      "Giấy tờ việc làm, học tập hoặc gia đình phù hợp với diện xin.",
      "Ảnh theo quy cách hiện hành.",
      "Giấy ủy quyền nếu nộp thay.",
    ],
    pitfalls: [
      "Nộp sai văn phòng có thẩm quyền.",
      "Địa chỉ không khớp giữa TM.30, hợp đồng thuê và đơn xin.",
      "Để quá hạn lưu trú trong lúc thu thập chứng cứ.",
      "Dựa vào bảng kiểm cũ trên mạng thay vì bản hiện hành của văn phòng.",
    ],
    remote: "Chuẩn bị và tư vấn từ xa; buổi hẹn tại quầy phải có mặt.",
  },
  estate: {
    label: "Dịch vụ di sản, di chúc và thừa kế",
    definition:
      "Việc thừa kế tại Thái Lan kết hợp thủ tục tòa án và đăng bộ: di chúc (hoặc chia theo luật), tòa chỉ định người quản lý di sản, rồi sang tên từng loại tài sản tại cơ quan đăng ký tương ứng. Di sản xuyên biên giới cần thêm dịch và hợp pháp hóa.",
    chain: [
      "Lập bản đồ di sản: tài sản, nơi có tài sản, hàng thừa kế và di chúc nếu có.",
      "Xác định tài sản nào bắt buộc phải có người quản lý di sản do tòa chỉ định.",
      "Tập hợp chứng cứ hộ tịch và quan hệ thừa kế, kèm bản dịch có chứng nhận cho giấy tờ nước ngoài.",
      "Nộp đơn xin chỉ định người quản lý di sản và dự phiên tòa qua luật sư.",
      "Sang tên từng tài sản tại cơ quan đăng ký với quyết định của tòa.",
    ],
    checklist: [
      "Giấy chứng tử và bản dịch có chứng nhận nếu cấp ở nước ngoài.",
      "Di chúc (nếu có) và nơi đang giữ bản gốc.",
      "Chứng cứ hàng thừa kế: sổ hộ khẩu, giấy khai sinh, giấy kết hôn.",
      "Chứng cứ tài sản: sổ đỏ, sao kê ngân hàng, sổ cổ đông.",
      "Hộ chiếu hoặc CCCD của tất cả người thừa kế.",
    ],
    pitfalls: [
      "Cố sang tên ngân hàng hoặc đất đai trước khi có quyết định chỉ định người quản lý di sản.",
      "Giấy tờ nước ngoài chưa có bản dịch tiếng Thái được hợp pháp hóa.",
      "Bỏ sót người thừa kế, có thể khiến việc phân chia vô hiệu.",
      "Tưởng di chúc nước ngoài tự động có hiệu lực với đất tại Thái Lan.",
    ],
    remote:
      "Tập hợp và soạn thảo giấy tờ từ xa; luật sư dự phiên tòa, bạn chỉ cần có mặt khi tòa yêu cầu.",
  },
  civilreg: {
    label: "Dịch vụ giấy tờ hộ tịch",
    definition:
      "Hộ tịch gồm khai sinh, kết hôn, ly hôn, khai tử, đổi tên và sổ hộ khẩu do văn phòng quận/huyện quản lý. Mỗi lần trích lục hay đính chính có danh mục chứng cứ riêng, và dùng ở nước ngoài thì cần thêm dịch và hợp pháp hóa.",
    chain: [
      "Xác định loại hồ sơ và văn phòng quận/huyện đang lưu giữ.",
      "Xin bản trích lục có chứng nhận hoặc nộp đơn đính chính.",
      "Tập hợp chứng cứ theo danh mục của văn phòng.",
      "Dịch có chứng nhận để dùng ở nước ngoài.",
      MFA,
    ],
    checklist: [
      "CCCD và sổ hộ khẩu Thái, hoặc hộ chiếu với người nước ngoài.",
      "Giấy chứng nhận hoặc trích lục hiện có, nếu bạn đang giữ.",
      "Chứng cứ hỗ trợ việc đính chính (giấy tờ trường học, bệnh viện hoặc tòa án).",
      "Giấy ủy quyền nếu chúng tôi xin thay bạn.",
    ],
    pitfalls: [
      "Xin tại văn phòng không lưu giữ hồ sơ gốc.",
      "Nộp đơn đính chính mà không có chứng cứ về dữ liệu đúng.",
      "Dịch bản trích lục rồi sau đó bị cấp lại, phải làm vòng hai.",
      "Phiên âm không thống nhất giữa giấy tờ của các thành viên trong gia đình.",
    ],
    remote:
      "Xin và theo dõi chúng tôi làm thay; một số trường hợp đính chính cần chính chủ có mặt.",
  },
  family: {
    label: "Dịch vụ giấy tờ luật gia đình",
    definition:
      "Các việc gia đình — ly hôn, quyền nuôi con, nhận cha cho con, giám hộ, thỏa thuận tiền hôn nhân — được giải quyết tại quận/huyện (khi thuận tình) hoặc tại tòa án. Chọn đúng con đường ngay từ đầu tránh được phần lớn chậm trễ.",
    chain: [
      "Đánh giá việc có thể làm thuận tình tại quận/huyện hay bắt buộc ra tòa.",
      "Soạn thỏa thuận hoặc đơn khởi kiện bằng câu chữ có hiệu lực thi hành.",
      "Tập hợp chứng cứ hộ tịch, kèm bản dịch có chứng nhận với giấy tờ nước ngoài.",
      "Đăng ký tại quận/huyện hoặc nộp đơn ra tòa.",
      "Xin bản sao có chứng nhận, dịch và hợp pháp hóa để dùng ở nước ngoài.",
    ],
    checklist: [
      "Giấy chứng nhận kết hôn và thỏa thuận tiền hôn nhân nếu có.",
      "Giấy khai sinh của các con.",
      "CCCD, sổ hộ khẩu và hộ chiếu của cả hai bên.",
      "Chứng cứ liên quan tới quyền nuôi con hoặc cấp dưỡng.",
      "Bản án nước ngoài (nếu có) kèm bản dịch có chứng nhận.",
    ],
    pitfalls: [
      "Ly hôn thuận tình nhưng không có thỏa thuận bằng văn bản về tài sản và con cái.",
      "Dùng bản án nước ngoài tại Thái Lan mà chưa qua bước công nhận.",
      "Câu chữ mà sau đó quận/huyện hoặc ngân hàng từ chối thực hiện.",
      "Tưởng hồ sơ Thái và nước ngoài tự động cập nhật cho nhau — không hề.",
    ],
    remote: "Soạn thảo và rà soát chứng cứ từ xa; đăng ký và phiên tòa cần có mặt.",
  },
  accounting: {
    label: "Dịch vụ giấy tờ kế toán, thuế và kiểm toán",
    definition:
      "Công việc gồm sổ sách kế toán, báo cáo tài chính, hồ sơ kiểm toán và tờ khai thuế, cùng việc chứng nhận và dịch chúng khi ngân hàng, hội đồng đấu thầu hoặc công ty mẹ ở nước ngoài yêu cầu.",
    chain: [
      "Xác nhận bên yêu cầu cần báo cáo nào và cho kỳ nào.",
      "Đối chiếu sổ sách và lập bộ báo cáo.",
      "Kiểm toán viên ký khi cần bản báo cáo đã kiểm toán.",
      "Dịch có chứng nhận báo cáo và thuyết minh kiểm toán.",
      "Công chứng và, nếu dùng ở nước ngoài, hợp pháp hóa tại Bộ Ngoại giao.",
    ],
    checklist: [
      "Affidavit công ty và giấy đăng ký thuế.",
      "Bảng cân đối thử, sổ cái và sao kê ngân hàng của kỳ báo cáo.",
      "Báo cáo và tờ khai thuế năm trước đã nộp.",
      "Yêu cầu về định dạng của bên tiếp nhận.",
    ],
    pitfalls: [
      "Nộp báo cáo quản trị trong khi cần bộ đã kiểm toán.",
      "Bản dịch làm mất câu chữ trong ý kiến kiểm toán.",
      "Chứng nhận bản tạm mà sau đó phải điều chỉnh lại.",
      "Lỡ lịch nộp theo luật trong lúc còn đang gom giấy tờ.",
    ],
    remote:
      "Xử lý từ xa trọn gói, bộ bản giấy có chứng nhận gửi chuyển phát.",
  },
  interpreter: {
    label: "Dịch vụ phiên dịch tại tòa và tại buổi hẹn",
    definition:
      "Phiên dịch là hỗ trợ ngôn ngữ nói tại buổi hẹn — phiên tòa, văn phòng đất đai, quận/huyện, buổi ký công chứng, quầy ngân hàng hay xuất nhập cảnh. Khác với dịch tài liệu có chứng nhận, và nhiều buổi hẹn cần cả hai.",
    chain: [
      "Xác nhận địa điểm, cặp ngôn ngữ, ngày và thời lượng dự kiến.",
      "Cung cấp thông tin chuyên môn và thuật ngữ cho phiên dịch viên trước.",
      "Gửi bộ tài liệu để thuật ngữ nói khớp với hồ sơ viết.",
      "Phiên dịch viên đi cùng bạn tới buổi hẹn.",
      "Ghi chú sau buổi hẹn và bản dịch viết nếu nơi đó yêu cầu.",
    ],
    checklist: [
      "Địa điểm, phòng và giờ hẹn.",
      "Cặp ngôn ngữ và phương ngữ nếu có yêu cầu.",
      "Tài liệu sẽ trao đổi, gửi trước.",
      "Tên và vai trò của các bên tham dự để xưng hô đúng.",
    ],
    pitfalls: [
      "Đặt quá muộn với cặp ngôn ngữ hiếm.",
      "Không có tài liệu trước, thuật ngữ phải ứng biến ngay tại quầy.",
      "Tưởng phiên dịch viên có thể chứng nhận luôn bản dịch viết tại chỗ.",
      "Ước lượng thiếu thời lượng, ảnh hưởng lịch trình.",
    ],
    remote:
      "Phiên dịch từ xa khả thi nếu nơi tiếp nhận cho phép; tòa án và cơ quan đăng ký thường yêu cầu có mặt.",
  },
  embassy: {
    label: "Dịch vụ giấy tờ đại sứ quán và lãnh sự",
    definition:
      "Chứng nhận tại đại sứ quán là mắt xích cuối của nhiều hồ sơ đi nước ngoài: sau hợp pháp hóa tại Bộ Ngoại giao, đại sứ quán nước đến tại Thái Lan chứng nhận giấy tờ. Mỗi đại sứ quán có quy định đặt hẹn, câu chữ và danh sách biên dịch riêng.",
    chain: [
      "Xác nhận yêu cầu hiện hành của đại sứ quán, gồm danh sách biên dịch được chấp nhận.",
      "Chuẩn bị bản gốc tiếng Thái và bản dịch có chứng nhận tương ứng.",
      "Hoàn tất hợp pháp hóa Bộ Ngoại giao trước, nếu đại sứ quán yêu cầu.",
      "Đặt hẹn, nộp tại đại sứ quán và nhận bộ đã chứng nhận.",
      "Bàn giao kèm bảng kiểm để nộp cho cơ quan tiếp nhận.",
    ],
    checklist: [
      "Bản gốc tiếng Thái có chữ ký và con dấu.",
      "Bản dịch có chứng nhận sang ngôn ngữ yêu cầu.",
      "Hộ chiếu hoặc CCCD của người đứng tên giấy tờ.",
      "Mẫu đơn của đại sứ quán và thư ủy quyền nếu có.",
    ],
    pitfalls: [
      "Dùng biên dịch viên ngoài danh sách được đại sứ quán chấp nhận.",
      "Nộp đại sứ quán trước khi hợp pháp hóa Bộ Ngoại giao.",
      "Thiếu mẫu đơn hoặc ảnh theo quy định riêng của đại sứ quán.",
      "Tưởng mọi đại sứ quán theo cùng một trình tự — không phải vậy.",
    ],
    remote:
      "Chuẩn bị và nộp chúng tôi làm thay ở nơi đại sứ quán cho phép đại diện.",
  },
  ip: {
    label: "Dịch vụ giấy tờ nhãn hiệu, bản quyền và sở hữu trí tuệ",
    definition:
      "Công việc SHTT tại Thái Lan xoay quanh đăng ký và duy trì nhãn hiệu tại Cục Sở hữu trí tuệ, cùng việc ghi nhận bản quyền và các văn bản chuyển nhượng hay li-xăng — nhiều văn bản trong đó cần công chứng và hợp pháp hóa khi ký ở nước ngoài.",
    chain: [
      "Tra cứu khả năng đăng ký và chọn nhóm sản phẩm/dịch vụ trước khi nộp.",
      "Chuẩn bị đơn, mẫu nhãn và giấy ủy quyền.",
      "Nộp và theo dõi thẩm định; trả lời thông báo của cơ quan.",
      "Chứng nhận và hợp pháp hóa giấy ủy quyền, hợp đồng chuyển nhượng hoặc li-xăng ký ở nước ngoài.",
      "Ghi nhận chuyển nhượng, li-xăng và gia hạn đúng hạn.",
    ],
    checklist: [
      "Mẫu nhãn đúng dạng sẽ sử dụng thực tế.",
      "Giấy tờ doanh nghiệp hoặc CCCD của người nộp đơn.",
      "Danh mục hàng hóa, dịch vụ để chọn nhóm.",
      "Giấy ủy quyền đã ký (công chứng và hợp pháp hóa nếu ký ở nước ngoài).",
      "Chứng cứ sử dụng đầu tiên, nếu viện dẫn.",
    ],
    pitfalls: [
      "Nộp mà không tra cứu trước, sau đó bị phản đối.",
      "Danh mục nhóm quá hẹp so với hoạt động kinh doanh thực tế.",
      "Giấy ủy quyền ký ở nước ngoài thiếu chứng nhận cần thiết.",
      "Trễ hạn gia hạn hoặc hạn trả lời cơ quan.",
    ],
    remote:
      "Nộp và theo đuổi đơn làm từ xa; chỉ khâu chứng nhận chữ ký có thể cần có mặt.",
  },
  litigation: {
    label: "Hỗ trợ tố tụng và giấy tờ tòa án",
    definition:
      "Hỗ trợ tố tụng gồm bản tự khai, bộ chứng cứ, bản dịch có chứng nhận cho chứng cứ tiếng nước ngoài, affidavit công chứng và giấy ủy quyền — chuẩn bị sao cho tòa chấp nhận mà không bị phản đối về hình thức.",
    chain: [
      "Đánh giá vụ việc: thẩm quyền, thời hiệu và khoảng trống chứng cứ.",
      "Soạn bản tự khai và tập hợp chứng cứ theo mẫu của tòa.",
      "Dịch có chứng nhận từng tài liệu tiếng nước ngoài.",
      "Công chứng và hợp pháp hóa các văn bản ký ở nước ngoài.",
      "Nộp hồ sơ, luật sư dự phiên tòa và các bước sau bản án.",
    ],
    checklist: [
      "Hợp đồng, thư từ trao đổi và toàn bộ chuỗi tài liệu.",
      "Hộ chiếu hoặc giấy tờ doanh nghiệp của các bên.",
      "Giấy ủy quyền cho luật sư (có chứng nhận nếu ký ở nước ngoài).",
      "Bản án nước ngoài hoặc phán quyết trọng tài, kèm bản dịch có chứng nhận.",
    ],
    pitfalls: [
      "Nộp chứng cứ tiếng nước ngoài mà không có bản dịch tiếng Thái có chứng nhận.",
      "Giấy ủy quyền ký ở nước ngoài chưa công chứng và hợp pháp hóa.",
      "Để hết thời hiệu trong lúc thu thập chứng cứ.",
      "Kỳ vọng được cam kết kết quả — không luật sư nào cam kết được.",
    ],
    remote: "Soạn thảo và xử lý chứng cứ từ xa; luật sư dự phiên tòa.",
  },
  pdpa: {
    label: "Dịch vụ hồ sơ PDPA và bảo vệ dữ liệu cá nhân",
    definition:
      "Luật Bảo vệ dữ liệu cá nhân (PDPA) của Thái Lan yêu cầu doanh nghiệp có căn cứ được ghi nhận bằng văn bản khi xử lý dữ liệu cá nhân: thông báo quyền riêng tư, hồ sơ đồng ý, hợp đồng xử lý dữ liệu, sổ ghi hoạt động xử lý và quy trình xử lý sự cố.",
    chain: [
      "Lập bản đồ dữ liệu: bạn giữ dữ liệu cá nhân nào, vì sao và luân chuyển tới đâu.",
      "Chọn và ghi nhận căn cứ pháp lý cho từng mục đích xử lý.",
      "Soạn thông báo quyền riêng tư, mẫu đồng ý và hợp đồng xử lý dữ liệu.",
      "Thiết lập sổ ghi hoạt động xử lý và quy trình xử lý sự cố rò rỉ.",
      "Đào tạo nhân sự và đặt chu kỳ rà soát khi hoạt động xử lý thay đổi.",
    ],
    checklist: [
      "Danh sách hệ thống và nhà cung cấp đang giữ dữ liệu cá nhân.",
      "Thông báo quyền riêng tư và câu chữ đồng ý hiện có.",
      "Hợp đồng với nhà cung cấp có nội dung xử lý dữ liệu.",
      "Thông tin chuyển dữ liệu xuyên biên giới, nếu có.",
    ],
    pitfalls: [
      "Sao chép thông báo GDPR nước ngoài mà không đối chiếu yêu cầu PDPA.",
      "Gộp chung nội dung đồng ý khiến không thể viện dẫn.",
      "Không có sổ ghi hoạt động xử lý — thứ bị hỏi đầu tiên khi rà soát.",
      "Hợp đồng nhà cung cấp không có điều khoản xử lý dữ liệu.",
    ],
    remote: "Hoàn toàn từ xa, các buổi làm việc qua video call.",
  },
  "misc-official": {
    label: "Các dịch vụ giấy tờ chính thức khác",
    definition:
      "Nhóm này gồm những giấy tờ chính thức không thuộc riêng một bộ ngành: giấy phép, chứng chỉ, giấy xác nhận, yêu cầu trích lục hồ sơ cùng việc chứng nhận và dịch để dùng trong hoặc ngoài Thái Lan.",
    chain: [
      "Xác định cơ quan cấp và bộ mẫu hiện hành.",
      "Tập hợp danh mục chứng cứ và kiểm tra giấy tờ tùy thân.",
      "Nộp và theo dõi theo số tham chiếu.",
      "Dịch có chứng nhận khi giấy tờ mang ra nước ngoài.",
      MFA,
    ],
    checklist: [
      "Hộ chiếu hoặc CCCD Thái của người nộp.",
      "Giấy chứng nhận, giấy phép hoặc số tham chiếu hiện có.",
      "Chứng cứ kèm theo nêu trong mẫu của cơ quan.",
      "Giấy ủy quyền để nộp thay.",
    ],
    pitfalls: [
      "Nộp sai cơ quan, phải xếp hàng lại từ đầu.",
      "Thiếu chứng cứ trong danh mục.",
      "Dịch trước khi bản gốc tiếng Thái chốt xong.",
      "Bỏ qua thời hạn hiệu lực của giấy tờ kèm theo.",
    ],
    remote: "Nộp và theo dõi chúng tôi làm thay ở nơi cho phép đại diện.",
  },
  council: {
    label: "Dịch vụ chứng nhận theo Hội đồng Luật sư Thái Lan",
    definition:
      "Một số việc chứng nhận chỉ do luật sư được Hội đồng Luật sư Thái Lan cấp phép thực hiện — gồm công việc của Notarial Services Attorney và việc luật sư chứng nhận tài liệu, bản sao để dùng ở nước ngoài.",
    chain: [
      "Xác nhận cơ quan tiếp nhận muốn chứng nhận nội dung gì và bằng câu chữ nào.",
      "Kiểm tra danh tính và thẩm quyền của người ký.",
      "Luật sư có giấy phép chứng nhận và vào sổ đăng ký.",
      MFA,
      "Bàn giao bản gốc đã chứng nhận kèm bảng kiểm.",
    ],
    checklist: [
      "Hộ chiếu hoặc CCCD Thái của từng người ký.",
      "Văn bản chưa ký ở bản cuối cùng.",
      "Giấy tờ thẩm quyền doanh nghiệp nếu ký nhân danh công ty.",
      "Yêu cầu bằng văn bản của cơ quan tiếp nhận, nếu có.",
    ],
    pitfalls: [
      "Văn bản đã ký trước buổi hẹn chứng nhận.",
      "Chứng nhận bản sao trong khi cần bản gốc.",
      "Cách viết tên không khớp hộ chiếu.",
      "Bỏ qua hợp pháp hóa khi nơi đến vẫn yêu cầu.",
    ],
    remote: "Rà soát và các bước sau đó từ xa; chứng kiến ký cần có mặt.",
  },
  "due-diligence": {
    label: "Dịch vụ thẩm định và xác minh (due diligence)",
    definition:
      "Thẩm định là việc kiểm chứng những gì đối tác tuyên bố: tư cách pháp nhân, giám đốc và cổ đông, sổ đỏ, rủi ro tranh chấp và tính xác thực của giấy tờ — dựa trên hồ sơ đăng ký chính thức chứ không dựa vào lời cam đoan.",
    chain: [
      "Xác định phạm vi: pháp nhân, tài sản và các câu hỏi rủi ro cần trả lời.",
      "Trích xuất hồ sơ đăng ký (DBD, văn phòng đất đai, danh mục vụ án) trong phạm vi có thể.",
      "Xác minh tính xác thực của giấy tờ và thẩm quyền ký.",
      "Dịch có chứng nhận các phát hiện chính và tài liệu nguồn.",
      "Báo cáo bằng văn bản, xếp hạng vấn đề và khuyến nghị bước tiếp theo.",
    ],
    checklist: [
      "Tên, mã số đăng ký và địa chỉ đối tác.",
      "Số sổ đỏ hoặc mã định danh tài sản.",
      "Dự thảo hợp đồng và các cam đoan đã đưa ra.",
      "Giấy tờ đối tác đã cung cấp.",
    ],
    pitfalls: [
      "Tin vào giấy tờ do đối tác cung cấp mà không đối chiếu hồ sơ đăng ký.",
      "Bỏ qua bước kiểm tra thế chấp, hạn chế tại văn phòng đất đai.",
      "Ký hợp đồng trước khi báo cáo hoàn tất.",
      "Coi kết quả tra cứu là vĩnh viễn — hồ sơ vẫn thay đổi.",
    ],
    remote: "Tra cứu và báo cáo từ xa; đi thực địa sắp xếp theo yêu cầu.",
  },
  pension: {
    label: "Dịch vụ giấy tờ lương hưu và trợ cấp nước ngoài",
    definition:
      "Các cơ quan chi trả lương hưu, trợ cấp ở nước ngoài định kỳ yêu cầu bằng chứng còn sống (life certificate), xác nhận địa chỉ và tình trạng hôn nhân, thường phải có chứng nhận của luật sư hoặc công chứng và đôi khi hợp pháp hóa.",
    chain: [
      "Xác nhận đúng mẫu và cấp độ chứng nhận mà cơ quan chi trả yêu cầu.",
      "Điền mẫu và thu thập chứng cứ nhân thân, địa chỉ.",
      "Luật sư hoặc công chứng chứng nhận chữ ký và bản sao.",
      MFA,
      "Nộp hoặc chuyển phát tới cơ quan chi trả và xác nhận đã nhận.",
    ],
    checklist: [
      "Hộ chiếu và giấy tờ cư trú, xuất nhập cảnh tại Thái Lan.",
      "Mẫu của cơ quan chi trả, chưa ký.",
      "Bằng chứng địa chỉ tại Thái Lan.",
      "Giấy kết hôn hoặc hộ tịch nếu quyền lợi phụ thuộc vào đó.",
      "Thông tin ngân hàng theo định dạng được yêu cầu.",
    ],
    pitfalls: [
      "Ký giấy xác nhận còn sống trước buổi hẹn chứng nhận.",
      "Trễ hạn hằng năm, có thể bị tạm dừng chi trả.",
      "Cấp độ chứng nhận thấp hơn yêu cầu của cơ quan.",
      "Bằng chứng địa chỉ không khớp hồ sơ xuất nhập cảnh.",
    ],
    remote: "Mọi việc trừ khâu chứng kiến ký chúng tôi làm thay bạn.",
  },
  "work-permit": {
    label: "Dịch vụ hồ sơ giấy phép lao động",
    definition:
      "Giấy phép lao động Thái Lan do Cục Việc làm cấp dựa trên bộ chứng cứ của cả người sử dụng lao động và người lao động. Chúng tôi chuẩn bị, sắp xếp trình tự giấy tờ và tư vấn các bước visa đi kèm; quyết định vẫn thuộc cơ quan cấp phép.",
    chain: [
      "Xác nhận loại giấy phép, điều kiện của doanh nghiệp và danh mục giấy tờ hiện hành.",
      "Chuẩn bị giấy tờ doanh nghiệp: affidavit, báo cáo tài chính, chứng cứ tỷ lệ lao động.",
      "Chuẩn bị giấy tờ người lao động: bằng cấp, thư giới thiệu, giấy khám sức khỏe, ảnh.",
      "Dịch có chứng nhận và hợp pháp hóa bằng cấp nước ngoài khi cần.",
      "Nộp, theo dõi và khớp thời hạn giấy phép với visa hoặc lần gia hạn.",
    ],
    checklist: [
      "Affidavit doanh nghiệp, trích lục DBD và đăng ký VAT.",
      "Hộ chiếu người lao động với đúng diện visa.",
      "Bằng tốt nghiệp và bảng điểm, kèm bản dịch có chứng nhận.",
      "Giấy khám sức khỏe theo quy cách hiện hành.",
      "Hợp đồng lao động và bản mô tả công việc.",
    ],
    pitfalls: [
      "Nhập cảnh bằng visa du lịch trong khi diện xin giấy phép cần loại visa khác.",
      "Bằng cấp nước ngoài chưa dịch có chứng nhận và hợp pháp hóa.",
      "Doanh nghiệp chưa đáp ứng tỷ lệ lao động hoặc vốn tại thời điểm nộp.",
      "Ngày hết hạn giấy phép và visa lệch nhau.",
    ],
    remote:
      "Chuẩn bị và hỗ trợ nộp từ xa; khám sức khỏe và các bước tại quầy phải trực tiếp.",
  },
  expert: {
    label: "Dịch vụ hồ sơ chuyên sâu và trường hợp phức tạp",
    definition:
      "Một số hồ sơ không đi theo lộ trình chuẩn: chuỗi nhiều quốc gia, yêu cầu câu chữ bất thường, hồ sơ từng bị từ chối, hoặc giấy tờ cần nhiều lớp chứng nhận theo đúng một thứ tự. Những trường hợp này được xử lý theo hướng tư vấn dẫn dắt.",
    chain: [
      "Rà soát vụ việc: cơ quan tiếp nhận thực sự yêu cầu gì, bằng văn bản.",
      "Thiết kế chuỗi chứng nhận và thứ tự bắt buộc phải chạy.",
      "Khắc phục lỗi trong giấy tờ hiện có trước khi chứng nhận tiếp.",
      "Thực hiện chuỗi: dịch, công chứng, hợp pháp hóa Bộ Ngoại giao, đại sứ quán.",
      "Bàn giao kèm bản ghi nhớ nộp hồ sơ để cơ quan tiếp nhận dễ đối chiếu.",
    ],
    checklist: [
      "Toàn bộ giấy tờ đã làm, kể cả bản từng bị từ chối.",
      "Văn bản nêu lý do từ chối hoặc thư yêu cầu, nếu có.",
      "Hộ chiếu hoặc giấy tờ doanh nghiệp của các bên.",
      "Quốc gia đến, cơ quan tiếp nhận và thời hạn.",
    ],
    pitfalls: [
      "Lặp lại chuỗi chứng nhận từng bị từ chối mà không xử lý nguyên nhân.",
      "Chứng nhận sai thứ tự, khiến bản cuối cùng không được bao trùm.",
      "Làm theo thông tin truyền miệng thay vì câu chữ của chính cơ quan.",
      "Không chừa thời gian cho vòng thứ hai với hồ sơ phức tạp.",
    ],
    remote:
      "Đánh giá và triển khai chuỗi chứng nhận từ xa; chỉ khâu chứng kiến ký cần có mặt.",
  },
};
